import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { marketplaceApi } from '../utils/api';
import ChessDiagram from '../components/ChessDiagram';

// ── Quiz sub-mode ─────────────────────────────────────────────────────────────

function QuizMode({ cards, prefs, onExit }) {
  const [deck]   = useState(() => [...cards].sort(() => Math.random() - 0.5));
  const [idx, setIdx]               = useState(0);
  const [showSolution, setShowSolution] = useState(false);
  const [done, setDone]             = useState(false);

  if (done) {
    return (
      <div className="text-center mt-5">
        <h4>Quiz complete!</h4>
        <p className="text-muted">{deck.length} card{deck.length !== 1 ? 's' : ''} reviewed.</p>
        <button className="btn btn-primary" onClick={onExit}>Back to Deck</button>
      </div>
    );
  }

  const card = deck[idx];

  const next = () => {
    setShowSolution(false);
    if (idx + 1 >= deck.length) setDone(true);
    else setIdx(i => i + 1);
  };

  return (
    <div style={{ maxWidth: 600, margin: '0 auto' }}>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <span className="text-muted small">Card {idx + 1} of {deck.length}</span>
        <button className="btn btn-sm btn-outline-secondary" onClick={onExit}>Exit Quiz</button>
      </div>
      <div className="card shadow-sm">
        <div className="card-body">
          <h5 className="card-title mb-3">{card.name}</h5>
          {card.fen && (
            <div className="d-flex justify-content-center mb-3">
              <ChessDiagram
                fen={card.fen}
                prefs={{ ...prefs, diagramFlipped: card.flipped, diagramShowSideToMoveDisc: card.show_side_to_move }}
                size={220}
              />
            </div>
          )}
          {card.problem && (
            <div className="mb-3">
              <div className="fw-semibold small text-muted mb-1">Problem</div>
              <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>{card.problem}</p>
            </div>
          )}
          {showSolution && card.solution && (
            <div className="alert alert-success mb-0">
              <div className="fw-semibold small mb-1">Solution</div>
              <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>{card.solution}</p>
            </div>
          )}
        </div>
        <div className="card-footer bg-transparent d-flex gap-2">
          {!showSolution && (
            <button className="btn btn-sm btn-outline-primary" onClick={() => setShowSolution(true)}>
              Show Solution
            </button>
          )}
          <button className="btn btn-sm btn-primary ms-auto" onClick={next}>
            {idx + 1 >= deck.length ? 'Finish' : 'Next →'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function PurchasedDeckPage() {
  const { mpoId }             = useParams();
  const [searchParams]        = useSearchParams();
  const navigate              = useNavigate();
  const { token, prefs }      = useAuth();

  const [offering, setOffering]   = useState(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [descExpanded, setDescExpanded] = useState(true);
  const [quizMode, setQuizMode]   = useState(searchParams.get('mode') === 'quiz');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const all = await marketplaceApi.list(token);
      const found = (all || []).find(o => o.mpo_id === mpoId);
      if (!found) throw new Error('Purchased deck not found.');
      setOffering(found);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, mpoId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="text-center mt-5"><div className="spinner-border" /></div>;
  if (error)   return <div className="alert alert-danger mt-3">{error}</div>;
  if (!offering) return null;

  const cards = offering.flashcards || [];

  if (quizMode) {
    return (
      <>
        <div className="mb-4">
          <button className="btn btn-link ps-0 text-decoration-none text-muted small"
            onClick={() => navigate('/dashboard')}>
            ← Dashboard
          </button>
          <h4 className="mb-0">{offering.title} — Quiz</h4>
        </div>
        <QuizMode cards={cards} prefs={prefs} onExit={() => setQuizMode(false)} />
      </>
    );
  }

  return (
    <>
      {/* Back link */}
      <button className="btn btn-link ps-0 text-decoration-none text-muted small mb-2"
        onClick={() => navigate('/dashboard')}>
        ← Dashboard
      </button>

      {/* Title */}
      <h2 className="mb-3">Marketplace Purchase — {offering.title}</h2>

      {/* Description block */}
      {offering.description && (
        <div className="card mb-4 shadow-sm">
          <div className="card-header bg-transparent d-flex justify-content-between align-items-center py-2">
            <span className="fw-semibold small">Description</span>
            <button
              className="btn btn-sm btn-link p-0 text-decoration-none text-muted"
              onClick={() => setDescExpanded(e => !e)}
              aria-label={descExpanded ? 'Collapse description' : 'Expand description'}
            >
              {descExpanded ? '▲ Collapse' : '▼ Expand'}
            </button>
          </div>
          {descExpanded && (
            <div className="card-body py-2" style={{ fontSize: '0.9rem', whiteSpace: 'pre-wrap' }}>
              {offering.description}
            </div>
          )}
        </div>
      )}

      {/* Action buttons */}
      <div className="d-flex gap-2 mb-4">
        <button className="btn btn-primary btn-sm" onClick={() => setQuizMode(true)}>
          Quiz this Deck
        </button>
        <span className="text-muted small align-self-center ms-1">
          {cards.length} card{cards.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Card list */}
      {cards.length === 0 && (
        <p className="text-muted">No cards in this deck.</p>
      )}
      <div className="row row-cols-1 row-cols-md-2 g-3">
        {cards.map((card, idx) => (
          <div className="col" key={card.id}>
            <div className="card h-100 shadow-sm">
              <div className="card-body d-flex gap-3" style={{ minHeight: card.fen ? 220 : 'auto' }}>
                {card.fen && (
                  <div className="flex-shrink-0" style={{ lineHeight: 0, alignSelf: 'flex-start' }}>
                    <ChessDiagram
                      fen={card.fen}
                      prefs={{ ...prefs, diagramFlipped: card.flipped, diagramShowSideToMoveDisc: card.show_side_to_move }}
                      size={160}
                    />
                  </div>
                )}
                <div className="flex-grow-1 min-width-0">
                  <div className="d-flex align-items-center gap-2 mb-1">
                    <span style={{
                      background: 'var(--theme-brand-nav)', color: '#fff', borderRadius: '50%',
                      width: 22, height: 22, display: 'inline-flex', alignItems: 'center',
                      justifyContent: 'center', fontSize: '0.7rem', fontWeight: 700, flexShrink: 0,
                    }}>{idx + 1}</span>
                    <h6 className="mb-0">{card.name}</h6>
                  </div>
                  {card.problem && (
                    <p className="card-text text-muted small mb-1"
                       style={{ whiteSpace: 'pre-wrap', display: '-webkit-box', WebkitLineClamp: 4,
                                WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {card.problem}
                    </p>
                  )}
                  {card.solution && (
                    <p className="card-text small mb-0" style={{ color: '#2d6a4f',
                       display: '-webkit-box', WebkitLineClamp: 3,
                       WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      <span className="fw-semibold">Solution: </span>{card.solution}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
